import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, Film } from "lucide-react";
import "./cinema.css";
import { STAGES, checkState } from "./story";
import { useIsMobile, usePrefersReducedMotion, useTimeline } from "./useTimeline";
import { cue, setSound } from "./sound";
import { StagePanel } from "./hud/Panels";
import { Controls, StageNav } from "./hud/Controls";
import { About, AgentCard, AgentRoster, Journal } from "./hud/Sheets";
import { HeroFilm } from "./HeroFilm";
import { EnginePanel, HiveFlow } from "./HiveFlow";
import { ENGINES, ENGINE_ORDER, engineNow } from "./engines";
import { useEngineTour } from "./tour";

function useCues(tl, on) {
  const key = useRef("");
  const { beat, u, sc } = tl;
  let k = beat.id;
  if (beat.id === "verify") { const c = checkState(sc, u, beat.dur); k += `-${c.idx}-${c.phase}`; }
  if (beat.id === "purchase") k += u >= 0.55 ? "-confirm" : "";
  if (beat.id === "exit") k += u >= 0.6 ? "-confirm" : "";
  useEffect(() => {
    if (!on || key.current === k) { key.current = k; return; }
    key.current = k;
    if (k.endsWith("-pass")) cue("pass"); else if (k.endsWith("-fail")) cue("fail");
    else if (k.endsWith("-confirm")) cue("confirm"); else if (!k.includes("-")) cue(k === "exit" ? "exit" : "beat");
  }, [k, on]);
}

function useJournal(tl) {
  const [entries, setEntries] = useState([]);
  const last = useRef("");
  useEffect(() => {
    const id = `${tl.scenarioId}-${tl.beat.id}`;
    if (tl.beat.id === "repeat" && last.current !== id) {
      const { sc } = tl;
      setEntries((xs) => [{ sym: sc.token.sym, scenario: sc.label, pct: sc.result.pct, text: sc.result.text, kind: sc.result.kind }, ...xs].slice(0, 20));
    }
    last.current = id;
  }, [tl.beat.id, tl.scenarioId]); // eslint-disable-line react-hooks/exhaustive-deps
  return entries;
}

export function RippCinema() {
  const tl = useTimeline();
  const mobile = useIsMobile();
  const [osReduced] = usePrefersReducedMotion();
  const [ui, setUiState] = useState({ sound: false, reduced: osReduced, cinema: false, sheet: null, selected: null, panelOpen: !mobile });
  const [qs] = useState(() => new URLSearchParams(window.location.search));
  const clip = qs.get("clip") === "1";   // ?clip=1 → clean recording view for the weekly X engine clips (no controls)
  const [engine, setEngine] = useState(() => (ENGINES[qs.get("engine")] ? qs.get("engine") : "memes"));
  const touring = useEngineTour(tl, engine, setEngine, !clip);
  useEffect(() => { const s = Number(qs.get("speed")); if (s > 0) tl.setSpeed(s); if (clip) { tl.pause(); window.__clipStart = tl.restart; } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const setUi = useCallback((p) => setUiState((s) => ({ ...s, ...p })), []);
  const root = useRef(null);
  const [isFs, setFs] = useState(false);
  const entries = useJournal(tl);
  useCues(tl, ui.sound);

  useEffect(() => { setSound(ui.sound); return () => setSound(false); }, [ui.sound]);
  useEffect(() => { const f = () => setFs(!!document.fullscreenElement); document.addEventListener("fullscreenchange", f); return () => document.removeEventListener("fullscreenchange", f); }, []);
  const toggleFs = useCallback(() => { if (document.fullscreenElement) document.exitFullscreen(); else root.current?.requestFullscreen?.().catch(() => {}); }, []);

  useEffect(() => {
    const k = (e) => {
      if (e.target.closest?.("input,textarea") || e.metaKey || e.ctrlKey || e.altKey) return;
      const i = tl.stageIdx;
      if (e.code === "Space") { e.preventDefault(); tl.toggle(); }
      if (e.key === "ArrowRight") { const n = STAGES.slice(i + 1).find((s) => tl.stagesInPlay.has(s.id)); if (n) tl.seekStage(n.id); }
      if (e.key === "ArrowLeft") { const p = STAGES.slice(0, i).reverse().find((s) => tl.stagesInPlay.has(s.id)); if (p) tl.seekStage(p.id); }
      if (e.key === "c") setUi({ cinema: !ui.cinema });
      if (e.key === "f") toggleFs();
      if (e.key === "Escape") setUi({ sheet: null, selected: null });
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [tl, ui.cinema, setUi, toggleFs]);

  const onSelect = useCallback((id) => setUi({ selected: id }), [setUi]);
  const stage = STAGES[tl.stageIdx], stageId = tl.beat.stage || stage.id, meme = engine === "memes";
  const eNow = meme ? null : engineNow(engine, stageId);
  const panel = meme ? <StagePanel tl={tl} /> : <EnginePanel engine={engine} stage={stageId} />;
  return (
    <div ref={root} className={`cine-root ${ui.cinema ? "is-cinema" : ""}`} data-testid="cinema-root">
      <HeroFilm engine={engine} stage={tl.beat.stage || stage.id} reduced={ui.reduced} />
      {!ui.cinema && <HiveFlow engine={engine} stage={stageId} sc={tl.sc} mobile={mobile} />}
      <div className="cine-letterbox" />

      <header className="cine-top">
        <div className="flex items-center gap-3">
          <Link to="/ripp/engine" className="cine-brand" data-testid="cinema-brand-link">RIPP<span>ENGINE</span></Link>
          {meme && <span className="cine-badge is-scen hidden sm:inline-flex" data-testid="cinema-current-scenario">{tl.sc.label}</span>}
        </div>
        <nav className={`cine-engines ${touring ? "is-touring" : ""}`} aria-label="Engines" data-testid="cinema-engine-tabs">
          {ENGINE_ORDER.map((id) => (
            <button key={id} type="button" onClick={() => { setEngine(id); tl.restart(); }} className={engine === id ? "is-on" : ""} data-testid={`cinema-engine-${id}`}>{ENGINES[id].label}</button>
          ))}
          {touring && <span className="cine-tour" data-testid="cinema-auto-tour">Auto tour</span>}
        </nav>
        <div className="flex items-center gap-2">
        </div>
      </header>

      <section className="cine-title" data-avoid aria-live="polite" data-testid="cinema-stage-title">
        <div className="cine-mono text-[10px] md:text-[11px] tracking-[0.4em] text-violet-300/80">STAGE {String(tl.stageIdx + 1).padStart(2, "0")} / 07</div>
        <h1 className="cine-display text-3xl sm:text-5xl lg:text-6xl font-black leading-[0.95] mt-2">{meme ? stage.label : eNow.label}</h1>
        <p className="mt-3 max-w-md text-sm md:text-base text-white/70" data-testid="cinema-caption">{meme ? tl.beat.caption : eNow.caption}</p>
      </section>

      {!ui.cinema && !mobile && !clip && (
        <>
          <div className="cine-side" data-avoid>{panel}</div>
          {meme && <AgentRoster onSelect={onSelect} selected={ui.selected} />}
        </>
      )}

      <footer className="cine-bottom" data-avoid>
        {!ui.cinema && mobile && (
          <div className="cine-side">
            <button type="button" className="cine-side-toggle" onClick={() => setUi({ panelOpen: !ui.panelOpen })} data-testid="cinema-panel-toggle" aria-expanded={ui.panelOpen}>
              {ui.panelOpen ? <ChevronDown size={14} /> : <ChevronUp size={14} />} Stage details
            </button>
            {ui.panelOpen && panel}
            {meme && <AgentRoster onSelect={onSelect} selected={ui.selected} />}
          </div>
        )}
        {!ui.cinema && <StageNav tl={tl} labels={meme ? null : ENGINES[engine].stages} />}
        {ui.cinema
          ? <button type="button" className="cine-btn is-active self-end" onClick={() => setUi({ cinema: false })} data-testid="cinema-exit-view"><Film size={16} /><span className="cine-btn-label">Show instruments</span></button>
          : !clip && <Controls tl={tl} ui={ui} setUi={setUi} onFullscreen={toggleFs} isFs={isFs} showScenarios={meme} />}
      </footer>

      {ui.selected && <AgentCard id={ui.selected} sc={tl.sc} onClose={() => setUi({ selected: null })} />}
      {ui.sheet === "journal" && <Journal entries={entries} onClose={() => setUi({ sheet: null })} />}
      {ui.sheet === "about" && <About onClose={() => setUi({ sheet: null })} />}
    </div>
  );
}
